import React, { useState, useEffect } from 'react';
import { demoRepository } from '../../repository/demoRepository';
import { calculations } from '../../utils/calculations';
import SafeIcon from '../../common/SafeIcon';
import PropertyDocumentActions from '../documents/PropertyDocumentActions';
import SmartDateInput from '../ui/SmartDateInput';

const ALLOWED_DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'zip', 'png', 'jpg', 'jpeg'];
const ALLOWED_DOCUMENT_MIMES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip',
  'image/png',
  'image/jpeg',
  'image/jpg'
];
const MAX_DOCUMENT_SIZE = 15 * 1024 * 1024;

const insuranceDocumentTypeMap = {
  building: 'building_insurance_policy',
  landlord: 'landlord_insurance_policy',
  contents: 'contents_insurance_policy',
  public_liability: 'public_liability_certificate',
  strata: 'strata_insurance_certificate',
  other: 'insurance_certificate'
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const units = ['Bytes', 'KB', 'MB'];
  const index = Math.min(2, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
};

const getDocumentOpenUrl = (doc) => {
  if (!doc) return '';
  const fileNames = Array.isArray(doc.files) && doc.files.length > 0
    ? doc.files.filter(Boolean)
    : Array.isArray(doc.fileNames) && doc.fileNames.length > 0
      ? doc.fileNames.filter(Boolean)
      : doc.fileName ? [doc.fileName] : [];
  const baseUrl = (import.meta.env.VITE_POCKETBASE_URL || '').trim().replace(/\/$/, '');
  const mappedUrl = Array.isArray(doc.fileUrls)
    ? doc.fileUrls.find(url => typeof url === 'string' && url.length > 0)
    : '';
  if (mappedUrl?.startsWith('http')) return mappedUrl;
  if (mappedUrl?.startsWith('/') && baseUrl) return `${baseUrl}${mappedUrl}`;
  if (baseUrl && doc.id && fileNames.length > 0) {
    return `${baseUrl}/api/files/documents/${doc.id}/${encodeURIComponent(String(fileNames[0]))}`;
  }
  return '';
};

const InsuranceForm = ({ initialData, existingDocuments = [], onSubmit, onCancel, isSubmitting = false }) => {
  const [formData, setFormData] = useState({
    propertyId: '', provider: '', policyNumber: '', policyType: 'building',
    premium: '', startDate: new Date().toISOString().split('T')[0], expiryDate: '',
    coverage: '', excess: '', status: 'active', notes: '', documentIds: []
  });
  const [policyDocument, setPolicyDocument] = useState(null);
  const [documentError, setDocumentError] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...formData, ...initialData,
        startDate: initialData.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        expiryDate: initialData.expiryDate ? new Date(initialData.expiryDate).toISOString().split('T')[0] : ''
      });
    }
  }, [initialData]);

  const properties = demoRepository.getAllProperties().filter(p => p.status === 'Active' || p.status === 'active');

  const policyTypes = [
    { value: 'building', label: 'Building Insurance' },
    { value: 'landlord', label: 'Landlord Insurance' },
    { value: 'contents', label: 'Contents Insurance' },
    { value: 'public_liability', label: 'Public Liability' },
    { value: 'strata', label: 'Strata Insurance' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'renewal_due', label: 'Renewal Due' },
    { value: 'expired', label: 'Expired' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'claim_open', label: 'Claim Open' },
    { value: 'archived', label: 'Archived' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const extension = String(file.name || '').split('.').pop()?.toLowerCase();
    const allowed = ALLOWED_DOCUMENT_MIMES.includes(file.type) || ALLOWED_DOCUMENT_EXTENSIONS.includes(extension);
    if (!allowed) {
      setPolicyDocument(null);
      setDocumentError('Only PDF, DOCX, ZIP, PNG, JPG or JPEG files are allowed.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE) {
      setPolicyDocument(null);
      setDocumentError('The insurance document must be 15MB or smaller.');
      event.target.value = '';
      return;
    }
    setDocumentError('');
    setPolicyDocument(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const policyTypeLabel = policyTypes.find(item => item.value === formData.policyType)?.label || 'Insurance Policy';
    const documentTitle = [formData.provider, policyTypeLabel, formData.policyNumber].filter(Boolean).join(' - ');
    onSubmit({
      ...formData,
      premium: Number(formData.premium || 0),
      coverage: Number(formData.coverage || 0),
      excess: Number(formData.excess || 0),
      startDate: new Date(formData.startDate).getTime(),
      expiryDate: new Date(formData.expiryDate).getTime(),
      id: initialData?.id,
      attachment: policyDocument ? {
        file: policyDocument,
        title: documentTitle || policyDocument.name.replace(/\.[^/.]+$/, ''),
        documentType: insuranceDocumentTypeMap[formData.policyType] || 'insurance_certificate',
        documentDate: new Date(formData.startDate).getTime()
      } : null
    });
  };

  const inputClass = "min-h-[44px] w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";
  const daysToExpiry = formData.expiryDate ? calculations.calculateDaysToExpiry(new Date(formData.expiryDate).getTime()) : null;
  const expiryStatus = daysToExpiry !== null ? (daysToExpiry < 0 ? 'Expired' : daysToExpiry < 30 ? 'Expiring Soon' : 'Active') : 'Unknown';
  const expiryColor = expiryStatus === 'Expired' ? 'text-red-600' : expiryStatus === 'Expiring Soon' ? 'text-amber-600' : 'text-emerald-600';

  return (
    <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass}>Property *</label>
          <select name="propertyId" value={formData.propertyId} onChange={handleChange} className={inputClass} required>
            <option value="">Select property...</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Insurance Provider *</label>
          <input type="text" name="provider" value={formData.provider} onChange={handleChange} className={inputClass} placeholder="e.g., CGU, AIG, Allianz" required />
        </div>
        <div>
          <label className={labelClass}>Policy Number *</label>
          <input type="text" name="policyNumber" value={formData.policyNumber} onChange={handleChange} className={inputClass} placeholder="e.g., POL-12345678" required />
        </div>
        <div>
          <label className={labelClass}>Policy Type *</label>
          <select name="policyType" value={formData.policyType} onChange={handleChange} className={inputClass} required>
            {policyTypes.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Annual Premium ($) *</label>
          <input type="number" name="premium" value={formData.premium} onChange={handleChange} onFocus={(e) => { if (e.target.value === '0') e.target.select(); }} className={inputClass} step="0.01" min="0" required />
        </div>
        <div>
          <label className={labelClass}>Coverage Amount ($) *</label>
          <input type="number" name="coverage" value={formData.coverage} onChange={handleChange} onFocus={(e) => { if (e.target.value === '0') e.target.select(); }} className={inputClass} step="0.01" min="0" required />
        </div>
        <div>
          <label className={labelClass}>Excess Amount ($) *</label>
          <input type="number" name="excess" value={formData.excess} onChange={handleChange} onFocus={(e) => { if (e.target.value === '0') e.target.select(); }} className={inputClass} step="0.01" min="0" required />
        </div>
        <div>
          <label className={labelClass}>Status *</label>
          <select name="status" value={formData.status} onChange={handleChange} className={inputClass} required>
            {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Start Date *</label>
          <SmartDateInput name="startDate" value={formData.startDate} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Expiry Date *</label>
          <SmartDateInput name="expiryDate" value={formData.expiryDate} onChange={handleChange} className={inputClass} baseDate={formData.startDate} baseLabel="the policy start date" required />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Notes</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} className={inputClass} rows="3" placeholder="Additional policy details, special conditions, etc." />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Policy Document <span className="font-normal text-slate-400">(Optional)</span></label>
          {existingDocuments.length > 0 && !policyDocument && (
            <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 sm:p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-200">
                  <SafeIcon icon="CheckCircle" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-emerald-900">
                    {existingDocuments.length === 1 ? 'Policy document already attached' : `${existingDocuments.length} policy documents already attached`}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {existingDocuments.map(doc => (
                      <div key={doc.id} className="rounded-lg border border-emerald-100 bg-white/80 px-3 py-2">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="truncate text-xs font-semibold text-slate-800">{doc.title || doc.fileName || 'Insurance document'}</div>
                            <div className="mt-0.5 truncate text-[11px] text-slate-500">
                              {doc.fileName || (Array.isArray(doc.files) ? doc.files[0] : '') || 'Linked document'}
                            </div>
                            {doc.notes ? <div className="mt-1 line-clamp-2 text-[11px] text-slate-500"><span className="font-semibold text-slate-600">Notes:</span> {doc.notes}</div> : null}
                          </div>
                          <div className="flex-shrink-0">
                            <PropertyDocumentActions doc={doc} openUrl={getDocumentOpenUrl(doc)} touchFriendly />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-emerald-700">This document remains linked to the policy, Global Documents and the selected Property.</div>
                </div>
              </div>
            </div>
          )}
          <div className="relative rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 transition-colors hover:bg-slate-100">
            <input
              type="file"
              accept=".pdf,.docx,.zip,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/x-zip-compressed,image/png,image/jpeg"
              onChange={handleDocumentSelect}
              disabled={isSubmitting}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
              aria-label={existingDocuments.length > 0 ? "Add another insurance policy document" : "Attach insurance policy document"}
            />
            {policyDocument ? (
              <div className="flex items-center gap-3 pr-10">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white text-amber-600 shadow-sm ring-1 ring-slate-200">
                  <SafeIcon icon="FileText" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-800">{policyDocument.name}</div>
                  <div className="text-xs text-slate-500">{formatFileSize(policyDocument.size)} · Will be added to this policy and property</div>
                </div>
                <button
                  type="button"
                  onClick={(event) => { event.preventDefault(); event.stopPropagation(); setPolicyDocument(null); setDocumentError(''); }}
                  disabled={isSubmitting}
                  className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 hover:bg-white hover:text-red-600 disabled:opacity-50 sm:right-3"
                  aria-label="Remove newly selected insurance document"
                >
                  <SafeIcon icon="X" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center">
                <SafeIcon icon="UploadCloud" className="mb-2 text-2xl text-slate-400" />
                <div className="text-sm font-semibold text-slate-700">{existingDocuments.length > 0 ? 'Add another or updated policy document' : 'Attach the insurance policy document'}</div>
                <div className="mt-1 text-xs text-slate-500">PDF, DOCX, ZIP, PNG or JPG · max 15MB</div>
                <div className="mt-1 text-[11px] text-slate-400">{existingDocuments.length > 0 ? 'Your existing attachment will remain in place unless you add another document.' : 'After the policy saves, the file will also appear in Global Documents and the selected Property.'}</div>
                <div className="mt-1 text-[11px] text-slate-400">Images are smart-optimized on save when a smaller high-quality copy can be produced. PDFs are kept unchanged to preserve text quality and searchability.</div>
              </div>
            )}
          </div>
          {documentError && <p className="mt-1 text-xs text-red-600">{documentError}</p>}
        </div>
        {formData.expiryDate && (
          <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3.5 sm:p-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Policy Summary</h4>
            <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4 lg:gap-4">
              <div>
                <div className="text-slate-500 mb-1">Days to Expiry</div>
                <div className={`text-lg font-semibold ${expiryColor}`}>
                  {daysToExpiry !== null ? (daysToExpiry > 0 ? `${daysToExpiry} days` : 'Expired') : '-'}
                </div>
              </div>
              <div>
                <div className="text-slate-500 mb-1">Status</div>
                <div className="text-lg font-semibold capitalize">{expiryStatus}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-1">Monthly Cost</div>
                <div className="text-lg font-semibold">${(formData.premium / 12).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-1">Coverage Ratio</div>
                <div className="text-lg font-semibold">
                  {formData.coverage > 0 ? `${((formData.premium / formData.coverage) * 100).toFixed(3)}%` : '-'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
        <button type="button" onClick={onCancel} disabled={isSubmitting} className="min-h-[44px] w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 sm:w-auto">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="min-h-[44px] w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">{isSubmitting ? 'Saving Policy...' : 'Save Insurance Policy'}</button>
      </div>
    </form>
  );
};

export default InsuranceForm;